/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { LoggerModule } from "@vlinder-web/logger-module-react";
import _ from "lodash";
import { VApplication } from "../application";
import { ACCEPT_TERMS, IS_TERMS_ACCEPTED, GET_ORGANIZATION, PAYMENT_GATEWAY_URL, COMPLETE_KYC, UPDATE_COUNTRY } from "../pages/ProfileScreen/graphql";
import { SUBMIT_RAZORPAY_KYC } from "../pages/KYC/graphql/razorpay-kyc.mutation";
import { GraphqlServerModule } from '@vlinder-web/graphql-server-module-react';
import { humanize } from "uno-js";
import { data } from "./config/profile-data"
import { EDIT_PROFILE } from "../pages/ProfileScreen/graphql/edit-profile.mutation"
import { toast } from "react-hot-toast";
import { StorageModule } from "@vlinder-web/storage-module-react";
import {getErrorMessage} from "../utils/error.message";

const MODULE_NAME = "ProfileService";
const log: any = LoggerModule.getInstance();

const getGraphQLModule = () => {
    const app = VApplication.getInstance();
    return app.getSync('services.GraphqlServerModule') as GraphqlServerModule;
};

export class ProfileService {
    private static instance: ProfileService;
    private vapp: VApplication;
    private termsURL: string;
    private constructor(props: any = {}) {
        this.vapp = VApplication.getInstance();
        this.termsURL = props?.termsURL;
    }
    public static getInstance(props: any = {}): ProfileService {
        if (!ProfileService.instance) {
            ProfileService.instance = new ProfileService(props);
        }
        return ProfileService.instance;
    }



    async editProfile(data: any) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        log.blue(MODULE_NAME, `EDIT Profile  data in service`, data);
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: EDIT_PROFILE,
                    variables: {
                        input: data
                    },

                    fetchPolicy: "no-cache",
                });
                log.success(MODULE_NAME, "EDIT Profile: ", res);
                if (res) {
                    return res
                }
            }
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    async updateCountry(data: any) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        log.blue(MODULE_NAME, `Update Country data in service`, data);
        try {
            if (client) {
                // return "razorPay"
                const res = await client.mutate({
                    mutation: UPDATE_COUNTRY,
                    variables: {
                        nationality: data?.country
                    },
                    fetchPolicy: "no-cache",
                });
                log.success(MODULE_NAME, "update country: ", res);
                if (res) {
                    return res?.data?.updateMyNationality?.paymentOptions[0]?.provider
                }
            }
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    async checkTermsAcceptance() {
        const terms = ProfileService.getInstance();
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        console.log(`check terms and condition service`);
        try {
            // return false;
            if (client) {
                const res = await client.query({
                    query: IS_TERMS_ACCEPTED,
                    variables: {
                        input: {
                            type: "payment_gateway_kyc",
                            agreementUrl: terms?.termsURL
                        }
                    },
                    fetchPolicy: "network-only",
                });
                log.success(MODULE_NAME, "getOrganization: ", res);

                return res?.data?.isTAndCAccepted?.accepted
            }
        }
        catch (err: any) {
            getErrorMessage(err);
        }
    }

    async getPaymentGatewayType(url: string) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: PAYMENT_GATEWAY_URL,
                    fetchPolicy: "no-cache",
                    variables: {
                        returnUrl: url
                    }
                });
                log.success(MODULE_NAME, "payment gateway url ", res);
                if (res?.data?.onboardOnStripe) {
                    return res?.data?.onboardOnStripe?.url
                }
            }
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    async getCompleteKYCUrl(url: string) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: COMPLETE_KYC,
                    variables: {
                        returnUrl: url
                    },
                    fetchPolicy: "no-cache",
                });
                log.success(MODULE_NAME, "payment gateway url ", res);
                if (res?.data?.completeOnboarding) {
                    return res?.data?.completeOnboarding?.url
                }
            }
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    async acceptTerms(data: any) {
        const profileService = ProfileService.getInstance();
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        log.blue(MODULE_NAME, `Create fiat account data in service`, data);
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: ACCEPT_TERMS,
                    variables: {
                        input: {
                            type: "payment_gateway_kyc",
                            agreementUrl: profileService?.termsURL
                        }
                    },

                    fetchPolicy: "no-cache",
                });
                // log.success(MODULE_NAME, "EDIT Profile: ", res);
                if (res?.data) {
                    return res?.data?.acceptTAndC?.accepted
                }
            }
            return true
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    async createFiatAccount(data: any) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        log.blue(MODULE_NAME, `Create fiat account data in service`, data);
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: SUBMIT_RAZORPAY_KYC,
                    variables: {
                        input: {
                            name: data?.name,
                            tnc_accepted: true,
                            business_name: data?.businessName,
                            ifsc_code: data?.ifsc_code,
                            beneficiary_name: data?.beneficiaryName,
                            account_number: data?.accountNumber,
                            account_type: data?.accountType,
                            business_type: data?.businessType,
                        }
                    },
                    fetchPolicy: "no-cache",
                });
                log.success(MODULE_NAME, "Razor pay Kyc", res);
                if (res?.data) {
                    return res?.data?.onboardOnRazorPay;
                }
            }
            return true
        } catch (err: any) {
           getErrorMessage(err);
        }
    }

    getMeta = (data: any) => {
        if (data) {
            let _output: any = [];

            for (const [key, value] of Object.entries(data)) {
                if (
                    key === "fullAddress" ||
                    key === "email" ||
                    key === "telephone" ||
                    key === "website" ||
                    key === "owner"
                ) {
                    _output.push({
                        key: humanize(key.toString()),
                        value: value ? value : "-",
                    });
                } else if (key === "sameAs") {
                    _output.push({
                        key: humanize("website"),
                        value: value ? value : "-",
                    });
                }
            }
            if (data?.paymentOptions && Array.isArray(data?.paymentOptions)) {
                data?.paymentOptions?.map(item => {
                    if (item?.provider) {
                        _output.push({
                            key: "Payment Provider",
                            value: item?.provider
                        },
                            {
                                key: "Account",
                                value: item?.account ? humanize(item?.account.toString()) : "-"
                            },
                            {
                                key: "KYC Submitted",
                                value: item?.KYCSubmitted ? "Yes" : "No"
                            },
                            {
                                key: "Payment Enabled",
                                value: item?.paymentEnabled ? "Yes" : "No"
                            }
                        )
                    }
                })
                // for (const [key, value] of Object.entries(data?.paymentOption)) {


                //     if (
                //         key === "paymentEnabled"
                //     ) {
                //         _output.push({
                //             key: humanize(key.toString()),
                //             value: value ? "Yes" : "No"
                //         });
                //     }
                //     if (key === "KYCSubmitted") {
                //         _output.push({
                //             key: "KYC Submitted",
                //             value: value ? "Yes" : "No"
                //         });
                //     }
                //     if (key === "account") {
                //         _output.push({
                //             key: humanize(key.toString()),
                //             value: value ? value : "-",
                //         });
                //     }
                // }
            }

            return _output;
        }
    };

    getButtons(profileData: any, country: string) {
        const storageService = StorageModule.getInstance();
        const userRole = storageService.get("userProfile", "session")?.value?.role?.name;
        console.log('user profile before buttons', profileData, userRole, country);
        if (profileData === null && country === null && userRole !== 'broker') {
            return [
                {
                    key: "editProfile",
                    title: "Edit",
                },
                // {
                //     key: "enableKyc",
                //     title: "Enable KYC"
                // }
            ]
        }
        if (Array.isArray(profileData) && profileData?.length === 1) {
            let _profileData = profileData?.find(item => item?.provider);
            if (!_profileData?.paymentEnabled && _profileData?.account === null) {
                return [
                    {
                        key: "editProfile",
                        title: "Edit",
                    },
                    {
                        key: "createFiatAccount",
                        title: "Create Fiat Account"
                    }
                ]
            }
            if (!_profileData?.KYCSubmitted || !_profileData?.paymentEnabled) {
                return [
                    {
                        key: "editProfile",
                        title: "Edit",
                    },
                    // {
                    //     key: "kyc",
                    //     title: "Complete your KYC"
                    // }
                ]
            }
            else {
                return [
                    {
                        key: "editProfile",
                        title: "Edit",
                    },
                ]
            }
        }
        if (Array.isArray(profileData) && profileData?.length > 1) {
            let _buttons = [{
                key: "editProfile",
                title: "Edit",
            }];
            profileData?.map(item => {
                console.log(`broker payments`, item);
                if ((item?.provider === 'razorpay') && (!item?.KYCSubmitted || !item?.paymentEnabled)) {
                    _buttons.push(
                        // {
                        //     key: "razorpay",
                        //     title: "Complete KYC for RazorPay"
                        // }
                    )
                }
                if ((item?.provider === 'stripe') && (!item?.KYCSubmitted || !item?.paymentEnabled)) {
                    _buttons.push(
                        // {
                        //     key: "stripe",
                        //     title: "Complete KYC for Stripe"
                        // }
                    )
                }
            })
            return _buttons;
        }
        else return [
            {
                key: "editProfile",
                title: "Edit",
            }
        ]

        // return [
        //     {
        //         key: "enableKyc",
        //         title: "Enable KYC"
        //     }
        // ]
    }

    async getProfile() {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient('graphql-gateway');
        try {
            if (client) {
                const res = await client.query({
                    query: GET_ORGANIZATION,
                    fetchPolicy: "network-only",
                });
                log.success(MODULE_NAME, "getOrganization: ", res);
                if(res?.data?.getMyOrganization){
                    return {
                        organizationData: res?.data?.getMyOrganization,
                        paymentOptions: res?.data?.getMyOrganization?.paymentOptions ?? [],
                        img: res?.data?.getMyOrganization?.logo,
                        title: res?.data?.getMyOrganization?.name,
                        description: res?.data?.getMyOrganization?.description,
                        key: res?.data?.getMyOrganization?.fullAddress,
                        meta: this.getMeta(res?.data?.getMyOrganization),
                        buttons: this.getButtons(res?.data?.getMyOrganization?.paymentOptions, res?.data?.getMyOrganization?.country)
                    };
                }
            }

        } catch (err) {
            // getErrorMessage(err);
        }
    }
}
